import { VideoEditorLogger } from '../utility/VideoEditorLogger';
import {
  CreateTrackCommand,
  CropTimelineCommand,
  RemoveTrackCommand,
  SetTimelineDurationCommand,
  ShiftElementCommand,
  ShiftMultipleElementsCommand,
  SplitElementCommand,
  TimelineMode,
  TrackLayerDownCommand,
  TrackLayerUpCommand,
  TranscriptPasteCommand,
  TranscriptRemoveCommand,
  TranscriptRestoreCommand,
  TranscriptSimpleInsertCommand,
  TranscriptSimpleRemoveCommand,
  TranscriptSimpleRestoreCommand,
  TrimElementEndCommand,
  TrimElementStartCommand,
} from './commands';
import { AddCompositionElementCommand } from './commands/AddCompositionElementCommand';
import { AddElementCommand } from './commands/AddElementCommand';
import { AddLayoutElementsCommand } from './commands/AddLayoutElementsCommand';
import { AddMediaElementCommand } from './commands/AddMediaElementCommand';
import { AddMusicElementCommand } from './commands/AddMusicElementCommand';
import { AddShapeElementCommand } from './commands/AddShapeElementCommand';
import { AddTextElementCommand } from './commands/AddTextElementCommand';
import { AddVideoElementCommand } from './commands/AddVideoElementCommand';
import { ChangeAspectRatioCommand } from './commands/ChangeAspectRatioCommand';
import { ClearLayoutElementsCommand } from './commands/ClearLayoutElementsCommand';
import { DeleteLayoutElementsCommand } from './commands/DeleteLayoutElementsCommand';
import { ExciseLayoutElementsCommand } from './commands/ExciseLayoutElementsCommand';
import { AddKaraokeBreakCommand } from './commands/karaoke/AddKaraokeBreakCommand';
import { AdjustKaraokeClipTimeCommand } from './commands/karaoke/AdjustKaraokeClipTimeCommand';
import { HideInKaraokeCommand } from './commands/karaoke/HideInKaraokeCommand';
import { ProduceKaraokeCommand } from './commands/karaoke/ProduceKaraokeCommand';
import { RemoveKaraokeBreakCommand } from './commands/karaoke/RemoveKaraokeBreakCommand';
import { ShowInKaraokeCommand } from './commands/karaoke/ShowInKaraokeCommand';
import { UpdateKaraokeConfigCommand } from './commands/karaoke/UpdateKaraokeConfigCommand';
import MergeLayoutElementsCommand from './commands/MergeLayoutElementsCommand';
import { MoveElementToTrackCommand } from './commands/MoveElementToTrackCommand';
import { RemoveElementCommand } from './commands/RemoveElementCommand';
import { SplitLayoutElementsCommand } from './commands/SplitLayoutElementsCommand';
import { SwitchLayoutTypeCommand } from './commands/SwitchLayoutTypeCommand';
import { TranscriptAddPunctuationCommand } from './commands/transcript/TranscriptAddPunctuationCommand';
import { TranscriptMultiSegmentTextReplaceCommand } from './commands/transcript/TranscriptMultiSegmentTextReplaceCommand';
import { TranscriptTextReplaceCommand } from './commands/transcript/TranscriptTextReplaceCommand';
import { TrimLayoutElementsCommand } from './commands/TrimLayoutElementsCommand';
import { UpdateElementCommand } from './commands/UpdateElementCommand';
import { UpdateMultipleElementsCommand } from './commands/UpdateMultipleElementsCommand';
import { CommandManager } from './lib/CommandManager';
import { ObserverManager } from './lib/ObserverManager';
import { TrackManager } from './lib/TrackManager';
import { TranscriptTrackManager } from './lib/TranscriptTrackManager';
import { Element } from './models/Element';
import { RemovedTranscriptClip } from './models/elements/RemovedTranscriptClip';
import { VideoTranscriptElement } from './models/elements/VideoTranscriptElement';
import {
  DEFAULT_EDITOR_KARAOKE_CONFIG,
  type KaraokeConfig,
} from './models/karaoke/KaraokeConfig';
import { KaraokeTrack } from './models/karaoke/KaraokeTrack';
import { LegacyTranscript } from './models/LegacyTranscript';
import { KARAOKE_TRACK_NUMBER, Timeline } from './models/Timeline';
import { Transcript } from './models/Transcript';
import { TranscriptTrack } from './models/TranscriptTrack';
import { RendererSynchronizer } from './RendererSynchronizer';
import { IdGenerator, IdGeneratorEntityType } from './services/IdGenerator';
import { fillInWhitespacesInTranscriptElements } from './services/punctuation';
import {
  TranscriptValidationError,
  verifyTranscriptConsistencyQuick,
} from './services/transcriptValidations';
import { EditorChangeType } from './types';

import type { Renderer } from '../renderer/Renderer';
import type {
  TranscriptElement,
  TranscriptElementLegacy,
} from '../types.ts/transcript';
import type { Command } from './commands';
import type { AddMusicElementOptions } from './commands/AddMusicElementCommand';
import type { AddVideoElementOptions } from './commands/AddVideoElementCommand';
import type { ResultElements as ExciseResultElements } from './commands/ExciseLayoutElementsCommand';
import type { ResultElements as SplitResultElements } from './commands/SplitLayoutElementsCommand';
import type { ResultElements as SwitchResultElements } from './commands/SwitchLayoutTypeCommand';
import type { IVideoEditor } from './interface/IVideoEditor';
import type { KnownPunctuation } from './interface/IVideoEditorCommands';
import type { ClipTranscriptSegment } from './models/ClipTranscriptSegment';
import type { HasClipTranscriptSegment } from './models/elements/HasClipTranscriptSegment';
import type { VideoElement } from './models/elements/VideoElement';
import type { Track } from './models/Track';
import type {
  ActiveTranscriptTokenWithClipSegmentAttributes,
  EditorObserver,
  ElementResizeResult,
} from './types';
import type { NaturalDimensions, VideoDimensions } from './types/aspectRatio';
import type { KaraokeData, KaraokeLine } from './types/karaoke';
import type { Video } from '@src/types.ts/story';

type KnownFlags =
  | 'headlessEnabled'
  | 'enableHeadlessEditorExperimental'
  | 'enableHeadlessEditorWave3'
  | 'enableHeadlessEditorWave4'
  | 'enableTranscriptSegmentArchitecture'
  | 'enableTranscriptDualWrite'
  | 'enableTranscriptMigrationValidation'
  | 'enableTranscriptMigrationSizeLimit'
  | 'enableNewKaraokeArchitecture';

export interface CutTranscriptSegment {
  elementId: string;
  initialIndexes: number[];
}

export interface VideoEditorConfig {
  maxMediaDurationToTrim: number;
}

export interface PasteTranscriptSegment {
  intoTime: number;
  startInitialIndex: number;
  endInitialIndex: number;
}

export interface RemovedSegment {
  fromTs: number;
  toTs: number;
}

const DEFAULT_MAX_MEDIA_DURATION_TO_TRIM = 1.5;

export class VideoEditor implements IVideoEditor {
  // Core state
  timeline: Timeline;
  legacyTranscript?: LegacyTranscript;
  private karaokeConfigs: Map<string, KaraokeConfig> = new Map();
  private defaultKaraokeConfigId: string | null = null;
  private originalTranscript: Transcript;
  private rendererSynchronizer: RendererSynchronizer;
  private commandManager: CommandManager;
  private trackManager: TrackManager;
  private transcriptTrackManager: TranscriptTrackManager; // manager for transcript track
  private transcriptTrackId?: string; // New property to store ID of track with transcript

  /**
   * Optional listener that will be called every time a command is successfully executed.
   * The listener can be used by host applications (e.g. VideoCreatorStore) to hook into
   * the editor's internal command execution and maintain a consolidated undo/redo stack.
   */
  public commandExecutedListener?: (command: Command) => void;

  // State management
  private batchDepth: number = 0; // Counter for backward compatibility
  private inBatch: boolean = false; // Simpler flag for batch operations
  private dirty: boolean = false; // Flag to track if timeline needs syncing
  private _isSyncingToRenderer: boolean = false; // For compatibility with existing code

  // Cut Paste
  // @deprecated
  private cutInitialIndexes: number[] = [];

  private cutPasteClipboardSegments: ClipTranscriptSegment[] | null = null;

  // Timeline editing mode
  private timelineMode: TimelineMode = TimelineMode.DEFAULT;

  // Observer pattern implementation (moved to ObserverManager)
  private observerManager: ObserverManager;

  private flags: Record<KnownFlags, boolean> = {
    headlessEnabled: false,
    enableHeadlessEditorExperimental: false,
    enableHeadlessEditorWave3: false,
    enableHeadlessEditorWave4: false,
    enableTranscriptSegmentArchitecture: false,
    enableTranscriptDualWrite: false,
    enableTranscriptMigrationValidation: true,
    enableTranscriptMigrationSizeLimit: true,
    enableNewKaraokeArchitecture: false,
  };

  private config: VideoEditorConfig = {
    maxMediaDurationToTrim: DEFAULT_MAX_MEDIA_DURATION_TO_TRIM,
  };

  // Legacy event handler (kept for backward compatibility)
  onStateChange?: (timeline: Timeline) => void;

  constructor() {
    this.timeline = new Timeline();
    this.rendererSynchronizer = new RendererSynchronizer(this);
    this.commandManager = new CommandManager(this);
    this.observerManager = new ObserverManager(this);

    // Create TrackManager with this instance as the editor
    this.trackManager = new TrackManager(this);
    this.transcriptTrackManager = new TranscriptTrackManager(
      this,
      this.trackManager,
    );

    // Initialize transcript with an empty array to avoid null references
    this.legacyTranscript = new LegacyTranscript([]);
    this.originalTranscript = new Transcript([]);

    // Initialize editor logging
    this.initializeLogging();
  }

  /**
   * Initialize editor logging settings
   * This can be called again to change logging configuration
   */
  initializeLogging(
    options: {
      enableCommandLogging?: boolean;
      enableTimelineLogging?: boolean;
      enableTranscriptLogging?: boolean;
      enableBatchLogging?: boolean;
      enablePerformanceLogging?: boolean;
      enableStateLogging?: boolean;
    } = {},
  ): void {
    // Check if we're in development environment
    const isDev = process.env.NODE_ENV === 'development';

    VideoEditorLogger.initialize({
      // In dev mode, enable most channels by default unless explicitly disabled
      commandsEnabled: options.enableCommandLogging ?? true,
      timelineEnabled: options.enableTimelineLogging ?? isDev,
      transcriptEnabled: options.enableTranscriptLogging ?? isDev,
      batchingEnabled: options.enableBatchLogging ?? isDev,
      performanceEnabled: options.enablePerformanceLogging ?? isDev,
      stateEnabled: options.enableStateLogging ?? isDev,
    });

    VideoEditorLogger.logStateChange('editor_initialized', {
      timelineId: this.timeline.id,
      hasTranscript: !!this.legacyTranscript,
      hasNewArchitectureTranscript: !!this.originalTranscript,
      environment: process.env.NODE_ENV,
      isDev,
    });
  }

  /**
   * Gets the track manager instance
   * @returns The TrackManager instance
   */
  getTrackManager(): TrackManager {
    return this.trackManager;
  }

  /**
   * Gets the transcript track manager instance
   * @returns The TranscriptTrackManager instance
   */
  getTranscriptTrackManager(): TranscriptTrackManager {
    return this.transcriptTrackManager;
  }

  /**
   * Gets the current timeline mode
   */
  getTimelineMode(): TimelineMode {
    return this.timelineMode;
  }

  /**
   * Sets the timeline mode
   */
  setTimelineMode(mode: TimelineMode): void {
    this.timelineMode = mode;
  }

  /**
   * Gets the current framerate
   */
  getFramerate(): number {
    return this.timeline.framerate;
  }

  /**
   * Sets the framerate
   */
  setFramerate(fps: number): void {
    this.timeline.setFramerate(fps);
    this.markDirty('framerate_change');

    // Notify observers about the framerate change
    this.notifyObservers(EditorChangeType.FRAMERATE_CHANGED, {
      fps: fps,
      previousFps: this.timeline.framerate,
    });
    // Also notify of general timeline change
    this.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
      source: 'framerate_change',
      fps: fps,
    });

    this.maybeSync();
  }

  /**
   * Gets the current snap-to-frame setting
   */
  getSnapToFrame(): boolean {
    return this.timeline.snapToFrame;
  }

  /**
   * Sets the snap-to-frame setting
   */
  setSnapToFrame(snapToFrame: boolean): void {
    const previousValue = this.timeline.snapToFrame;
    this.timeline.setSnapToFrame(snapToFrame);
    this.markDirty('snap_to_frame_change');

    // Notify observers about the snap setting change
    this.notifyObservers(EditorChangeType.SNAP_TO_FRAME_CHANGED, {
      snapToFrame: snapToFrame,
      previousValue: previousValue,
    });
    // Also notify of general timeline change
    this.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
      source: 'snap_to_frame_change',
      snapToFrame: snapToFrame,
    });

    this.maybeSync();
  }

  /**
   * Converts time in seconds to frame number based on the current framerate
   */
  timeToFrame(time: number): number {
    return this.timeline.timeToFrame(time);
  }

  /**
   * Converts frame number to time in seconds based on the current framerate
   */
  frameToTime(frame: number): number {
    return this.timeline.frameToTime(frame);
  }

  /**
   * Snaps time to the nearest frame boundary if snap-to-frame is enabled
   */
  snapTimeToFrame(time: number): number {
    return this.timeline.snapTimeToFrame(time);
  }

  /**
   * Executes a command and adds it to history
   * Delegates to the CommandManager
   * Uses executeInBatch to ensure proper batching
   */
  executeCommand(command: Command): boolean {
    return this.executeInBatch(() => {
      const startTime = performance.now();
      const result = this.commandManager.execute(command);
      const endTime = performance.now();

      // Log the execution result
      if (result) {
        VideoEditorLogger.logCommand(command, 'complete', {
          durationMs: endTime - startTime,
        });

        // Notify external listener (e.g., host application) that a command has been executed
        if (this.commandExecutedListener) {
          try {
            this.commandExecutedListener(command);
          } catch (err) {
            // Listener errors should not break editor functionality
            VideoEditorLogger.logCommand(command, 'error', { err });
          }
        }
      } else {
        VideoEditorLogger.logCommand(command, 'error');
      }

      return result;
    });
  }

  /**
   * Undoes the last command
   * Delegates to the CommandManager
   * Uses executeInBatch to ensure proper batching
   */
  undo(): boolean {
    console.warn(
      'Undo from headless not ready yet, videoCreator.undo() should be used instead',
    );
    const command = this.commandManager.getLastExecutedCommand();
    if (!command) {
      VideoEditorLogger.logStateChange('undo', { error: 'No command to undo' });
      return false;
    }

    return this.executeInBatch(() => {
      const result = this.commandManager.undo();
      VideoEditorLogger.logCommandUndo(command, result);
      return result;
    });
  }

  /**
   * Redoes a previously undone command
   * Delegates to the CommandManager
   * Uses executeInBatch to ensure proper batching
   */
  redo(): boolean {
    console.warn(
      'Redo from headless not ready yet, videoCreator.redo() should be used instead',
    );
    const command = this.commandManager.getNextUndoneCommand();
    if (!command) {
      VideoEditorLogger.logStateChange('redo', { error: 'No command to redo' });
      return false;
    }

    return this.executeInBatch(() => {
      const result = this.commandManager.redo();
      VideoEditorLogger.logCommandRedo(command, result);
      return result;
    });
  }

  /**
   * Clears the internal undo/redo history maintained by the CommandManager.
   * This is used by host applications (e.g. legacy VideoCreatorStore) when
   * they revert the full video state via snapshots.  After such a revert the
   * existing command history inside the headless editor is no longer valid,
   * so we wipe it to avoid allowing the user to redo commands that no longer
   * apply to the current timeline.
   */
  clearHistory(): void {
    this.commandManager.clearHistory();
  }

  /**
   * Initializes and attaches all built-in observers
   * Call this method to automatically set up the default observers
   */
  initializeBuiltInObservers(): void {
    // Create and register a renderer sync observer
    const rendererSyncObserver = {
      onEditorChange: (
        editor: VideoEditor,
        changeType: EditorChangeType,
        payload: any,
      ) => {
        // Sync to renderer for timeline changes (unless we're coming from the renderer)
        if (
          changeType === EditorChangeType.TIMELINE_CHANGED &&
          payload.source !== 'renderer_sync'
        ) {
          VideoEditorLogger.logStateChange('sync_to_renderer', {
            triggerChangeType: changeType,
            payload,
          });
          this.rendererSynchronizer.syncToRenderer();
        }
      },
    };
    this.registerObserver(rendererSyncObserver);

    // Create and register a transcript refresh observer
    const transcriptRefreshObserver = this.createTranscriptRefreshObserver();
    this.registerObserver(transcriptRefreshObserver);
  }

  /**
   * Registers an observer for specific change types
   * Delegates to ObserverManager
   * @param observer The observer to register
   * @param changeTypes The types of changes to observe (if none provided, observes all changes)
   */
  registerObserver(
    observer: EditorObserver,
    changeTypes?: EditorChangeType[],
  ): void {
    this.observerManager.registerObserver(observer, changeTypes);
  }

  /**
   * Unregisters an observer from specific change types
   * Delegates to ObserverManager
   * @param observer The observer to unregister
   * @param changeTypes The types of changes to unobserve (if none provided, unobserves all changes)
   */
  unregisterObserver(
    observer: EditorObserver,
    changeTypes?: EditorChangeType[],
  ): void {
    this.observerManager.unregisterObserver(observer, changeTypes);
  }

  /**
   * Notifies all registered observers about a change
   * Delegates to ObserverManager
   * @param changeType The type of change that occurred
   * @param payload Optional data associated with the change
   * @param immediate Optional flag to force immediate notification even during batching (use with caution)
   */
  notifyObservers(
    changeType: EditorChangeType,
    payload?: any,
    immediate: boolean = false,
  ): void {
    this.observerManager.notifyObservers(changeType, payload, immediate);

    // If this is a timeline-related change but not the general TIMELINE_CHANGED event,
    // also emit a TIMELINE_CHANGED event to ensure MobX reactivity is triggered
    if (
      changeType !== EditorChangeType.TIMELINE_CHANGED &&
      [
        EditorChangeType.ELEMENT_ADDED,
        EditorChangeType.ELEMENT_REMOVED,
        EditorChangeType.ELEMENT_UPDATED,
        EditorChangeType.ELEMENT_SHIFTED,
        EditorChangeType.ELEMENT_TRACK_CHANGED,
        EditorChangeType.ELEMENT_TRIMMED_START,
        EditorChangeType.ELEMENT_TRIMMED_END,
        EditorChangeType.TRACK_ADDED,
        EditorChangeType.TRACK_REMOVED,
      ].includes(changeType)
    ) {
      this.observerManager.notifyObservers(
        EditorChangeType.TIMELINE_CHANGED,
        {
          source: changeType,
          details: payload,
        },
        immediate,
      );
    }
  }

  /**
   * Checks if sync is needed and performs it if necessary
   * Only syncs if not in a batch operation and editor is dirty
   * Also processes any queued notifications after syncing
   */
  private maybeSync(): void {
    if (!this.isInBatch() && this.dirty) {
      this.rendererSynchronizer.syncToRenderer();
      this.dirty = false;

      // Process any pending notifications after syncing
      this.observerManager.processQueuedNotifications();
    }
  }

  /**
   * Returns the current batch depth
   * Exposed for CommandManager to use
   */
  getBatchDepth(): number {
    return this.batchDepth;
  }

  /**
   * Returns whether we're currently in a batch operation
   * Replacement for getBatchDepth() > 0 checks
   */
  isInBatch(): boolean {
    return this.batchDepth > 0;
  }

  /**
   * Executes an operation within a batch, ensuring proper begin/end batch handling
   * This is the preferred way to execute operations that need to be batched
   * @param operation The function to execute within the batch
   * @returns The result of the operation
   */
  executeInBatch<T>(operation: () => T): T {
    const wasTopLevelBatch = this.batchDepth === 0;

    // Start the batch if this is a top-level call
    if (wasTopLevelBatch) {
      this.beginBatch();
    }

    try {
      // Execute the operation
      return operation();
    } finally {
      // End the batch if we started it
      if (wasTopLevelBatch) {
        this.endBatch();
      }
    }
  }

  /**
   * Marks the timeline as dirty and requiring sync
   * Centralized method to ensure consistent behavior
   * Made public for CommandManager to use
   * @param source Descriptive source of the change for logging
   */
  markDirty(source: string = 'unknown'): void {
    if (!this.dirty) {
      // Replace console log with structured logging
      VideoEditorLogger.logStateChange('timeline_dirty', { source });

      this.dirty = true;
      this.rendererSynchronizer.markDirty(source);
    }
  }

  /**
   * Connects the editor to a Renderer instance
   */
  connectRenderer(renderer: Renderer): void {
    this.rendererSynchronizer.connectRenderer(renderer);
  }

  /**
   * Force a sync from the renderer state
   * This can be called externally to ensure state is up-to-date
   * @param rendererState The state from the renderer
   */
  syncFromRenderer(rendererState: any, newTimeline?: Timeline): void {
    if (!rendererState) {
      VideoEditorLogger.logStateChange('sync_from_renderer_error', {
        error: 'No renderer state provided',
      });
      return;
    }

    // Update our timeline reference with the new timeline if provided
    if (newTimeline) {
      this.timeline = newTimeline;
    } else {
      // Fallback to creating a new timeline if one wasn't provided
      this.timeline = Timeline.fromJSON(rendererState);
    }

    // Notify observers about the timeline change
    this.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
      source: 'renderer_sync',
    });

    // Legacy callback support
    if (this.onStateChange) {
      this.onStateChange(this.timeline);
    }
  }

  private isTranscriptTrackSubstitutableWith(track: Track): boolean {
    const currentVideoTranscriptTrack = this.getVideoTranscriptTrack();
    if (!currentVideoTranscriptTrack) return false;
    if (track.type !== 'video') return false;
    const currentTrackElements = currentVideoTranscriptTrack.elements;
    const newTrackElements = track.elements;

    if (currentTrackElements.length !== newTrackElements.length) return false;
    return currentTrackElements.every((curEl, pos) => {
      const newEl = newTrackElements[pos];
      return (
        curEl.time === newEl.time &&
        curEl.duration === newEl.duration &&
        curEl.trim_start === newEl.trim_start
      );
    });
  }

  private makeNewTranscriptTrackWithElementsFrom(track: Track) {
    const currentVideoTranscriptTrack = this.getVideoTranscriptTrack();

    if (currentVideoTranscriptTrack) {
      let videoElementIdx = -1;
      const mergedSegmentElements =
        currentVideoTranscriptTrack.transcriptSegmentElements.map(el => {
          if (el instanceof VideoTranscriptElement) {
            videoElementIdx += 1;
            return new VideoTranscriptElement({
              ...track.elements[videoElementIdx].getState(),
              clipTranscriptSegment: el.getClipTranscriptSegment(),
            });
          } else if (el instanceof RemovedTranscriptClip) {
            return new RemovedTranscriptClip({
              clipTranscriptSegment: el.clipTranscriptSegment,
            });
          }
          return el;
        });

      return new TranscriptTrack({
        id: track.id,
        name: track.name,
        type: 'video',
        transcriptSegmentElements: mergedSegmentElements,
      });
    }
    return track; // fallback
  }

  /**
   * Loads a project from JSON data
   */
  loadProject(json: Record<string, any>): void {
    const timeline = Timeline.fromJSON(json);

    // For now assume first video track is the transcript track
    const firstVideoTrack = timeline.tracks.find(
      track => track.type === 'video',
    );

    const currentVideoTranscriptTrack = this.getVideoTranscriptTrack();

    if (firstVideoTrack && currentVideoTranscriptTrack) {
      if (this.isTranscriptTrackSubstitutableWith(firstVideoTrack)) {
        console.debug(
          'VideoEditor: loadProject: transcript track elements unchanged, substituting',
        );
        const updatedTranscriptTrack =
          this.makeNewTranscriptTrackWithElementsFrom(firstVideoTrack);
        timeline.replaceTrack(firstVideoTrack?.id, updatedTranscriptTrack);

        const karaokeTrack = this.getKaraokeTrack();
        if (karaokeTrack) {
          VideoEditorLogger.logKaraoke('load_project_replace_karaoke_track', {
            trackId: karaokeTrack.id,
            clipsCount: karaokeTrack.karaokeClips.length,
          });
          timeline.replaceTrack(karaokeTrack.id, karaokeTrack);
          console.debug(
            'VideoEditor: loadProject: karaoke track elements unchanged, substituting',
          );
        }
      }
    }

    this.timeline = timeline;

    // trackId preserved
    if (firstVideoTrack) {
      this.setTranscriptTrack(firstVideoTrack?.id);
    }

    this.dirty = true;

    // Notify observers
    this.notifyObservers(EditorChangeType.PROJECT_LOADED, {
      timeline: this.timeline,
      source: json,
    });

    this.maybeSync();
  }

  /**
   * Creates a new track and adds it to the timeline
   * @param type The type of track to create
   * @returns The newly created track
   */
  commandCreateTrack(
    type: 'video' | 'audio' | 'text' | 'image' = 'video',
  ): Track {
    // Use command pattern for track creation
    const command = new CreateTrackCommand(this, type);
    this.executeCommand(command);

    // The command stores the created track, so we can access it
    return command.createdTrack!;
  }

  /**
   * Removes a track from the timeline
   * @param trackId The track or track ID to remove
   * @returns true if removed, false otherwise
   */
  commandRemoveTrack(trackId: Track | string): Track | null | undefined {
    // Use command pattern for track removal
    const command = new RemoveTrackCommand(this, trackId);
    const success = this.executeCommand(command);
    return success ? command.getRemovedTrack() : null;
  }

  /**
   * Adds an element to a track
   * @param trackId The ID of the track to add the element to
   * @param element The element to add
   * @param sync Whether to sync to the renderer immediately (default: true)
   */
  commandAddElementToTrack(trackId: string, element: Element): boolean {
    // Use command pattern for adding elements
    return this.executeCommand(new AddElementCommand(this, trackId, element));
  }

  /**
   * Creates a new element and adds it to a track
   */
  commandCreateAndAddElement(
    trackId: string,
    type: string,
    props: Record<string, any> = {},
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (props.time !== undefined) {
      props.time = this.timeline.snapTimeToFrame(props.time);
    }

    if (props.duration !== undefined && typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    const element = Timeline.createElement(type, props);

    // Use command pattern for adding the element
    const success = this.executeCommand(
      new AddElementCommand(this, trackId, element),
    );

    return success ? element : null;
  }

  /**
   * Updates an element's properties
   * @param elementId The ID of the element to update
   * @param key The property key to update
   * @param value The new value for the property
   * @returns true if successful, false otherwise
   */
  commandUpdateElementProperty(
    elementId: string,
    key: string,
    value: any,
  ): boolean {
    const props: Record<string, any> = {};
    props[key] = value;

    // Use UpdateElementCommand for property updates
    return this.executeCommand(
      new UpdateElementCommand(this, elementId, props),
    );
  }

  /**
   * Updates multiple element properties at once
   * Can either accept a partial Element object or a state record from getState()
   */
  commandUpdateElement(elementId: string, props: Record<string, any>): boolean {
    // Use factory method to handle potential type transitions
    return this.executeCommand(
      UpdateElementCommand.create(this, elementId, props),
    );
  }

  commandUpdateMultipleElements(
    updates: Map<string, Record<string, Partial<Element>>>,
  ): boolean {
    return this.executeCommand(
      new UpdateMultipleElementsCommand(this, updates),
    );
  }

  /**
   * Removes an element from the timeline
   */
  commandRemoveElement(elementId: string): boolean {
    // Use RemoveElementCommand for element removal
    return this.executeCommand(new RemoveElementCommand(this, elementId));
  }

  /**
   * Moves an element to a different track
   */
  commandMoveElementToTrack(elementId: string, targetTrackId: string): boolean {
    // Use MoveElementToTrackCommand for moving elements between tracks
    return this.executeCommand(
      new MoveElementToTrackCommand(this, elementId, targetTrackId),
    );
  }

  /**
   * Adds a media element to the timeline with collision detection and automatic track management.
   * Supports TimelineMode for different collision handling behaviors:
   * - DEFAULT: Creates new track on collision
   * - RIPPLE: Shifts subsequent elements to make room
   * - OVERLAP: Allows overlapping elements
   *
   * @param element The media element to add
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @returns AddMediaElementCommand instance if successful, null otherwise
   */
  commandAddMediaElement(
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
  ): AddMediaElementCommand | null {
    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddMediaElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
    );

    const success = this.executeCommand(command);
    return success ? command : null;
  }

  /**
   * Adds a music/audio element to the timeline with music-specific features:
   * - Fit to end: automatically adjusts duration to fill to timeline end
   * - Volume control: sets initial volume level
   * - Auto fade: applies fade in/out effects
   * - Smart track placement: fills gaps in existing music tracks before creating new ones
   *
   * @param element The music/audio element to add
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @param options Music-specific options (fitToEnd, volume, autoFade)
   * @returns AddMusicElementCommand instance if successful, null otherwise
   */
  commandAddMusicElement(
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
    options?: AddMusicElementOptions,
  ): AddMusicElementCommand | null {
    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddMusicElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
      options,
    );

    const success = this.executeCommand(command);
    return success ? command : null;
  }

  /**
   * Creates and adds a text element to the timeline with smart positioning and validation
   * @param props Text element properties and configuration
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @returns The created Element if successful, null otherwise
   */
  commandCreateTextElement(
    props: Record<string, any> = {},
    targetTrackId?: string,
    mode?: TimelineMode,
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (props.time !== undefined) {
      // This is to account for the fact that the snapTimeToFrame function
      // rounds up to the nearest frame and the time we get here can be in
      // between frames.
      //
      // Ideally we should always snap the timeline playhead to the nearest
      // frame so time values are always indexed at the start of a frame
      // but this is a quick fix.
      const shiftedTime = props.time - 1 / this.timeline.framerate;
      props.time = this.timeline.snapTimeToFrame(
        shiftedTime < 0 ? 0 : shiftedTime,
      );
    }

    if (props.duration !== undefined && typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    // Set default text properties if not provided
    const textProps = {
      type: 'text',
      duration: 4,
      font_size: '32',
      font_family: 'Inter',
      font_weight: '600',
      fill_color: 'rgba(0, 0, 0, 1)',
      background_color: 'rgba(255, 255, 255, 0.7)',
      background_align_threshold: '0%',
      background_border_radius: '0%',
      width: '76%',
      y: '60%',
      ...props,
    };

    const element = Timeline.createElement('text', textProps);

    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddTextElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
    );

    const success = this.executeCommand(command);
    return success ? element : null;
  }

  /**
   * Creates and adds a shape element to the timeline with smart positioning and validation
   * @param props Shape element properties and configuration
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @returns The created Element if successful, null otherwise
   */
  commandCreateShapeElement(
    props: Record<string, any> = {},
    targetTrackId?: string,
    mode?: TimelineMode,
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (props.time !== undefined) {
      const shiftedTime = props.time - 1 / this.timeline.framerate;
      props.time = this.timeline.snapTimeToFrame(
        shiftedTime < 0 ? 0 : shiftedTime,
      );
    }

    if (props.duration !== undefined && typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    // Set default text properties if not provided
    const shapeProps = {
      type: 'shape',
      x: '25%',
      y: '25%',
      duration: 4,
      width: '50%',
      height: '50%',
      x_anchor: '50%',
      y_anchor: '50%',
      path: 'M 0 0 L 100 0 L 100 100 L 0 100 L 0 0 Z',
      fill_color: '#575757',
      ...props,
    };

    const element = Timeline.createElement('shape', shapeProps);

    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddShapeElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
    );

    const success = this.executeCommand(command);
    return success ? element : null;
  }

  /**
   * Creates and adds a shape element to the timeline with smart positioning and validation
   * @param props Shape element properties and configuration
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @returns The created Element if successful, null otherwise
   */
  commandCreateCompositionElement(
    props: Record<string, any> = {},
    targetTrackId?: string,
    mode?: TimelineMode,
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (props.time !== undefined) {
      const shiftedTime = props.time - 1 / this.timeline.framerate;
      props.time = this.timeline.snapTimeToFrame(
        shiftedTime < 0 ? 0 : shiftedTime,
      );
    }

    if (props.duration !== undefined && typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    const element = Timeline.createElement('composition', props);

    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddCompositionElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
    );

    const success = this.executeCommand(command);
    return success ? element : null;
  }

  /**
   * Adds a video element to the timeline with video-specific features:
   * - Volume control: sets initial volume level
   * - Video fit: controls how video is resized to fit the element
   * - Loop control: enables/disables video looping
   * - Auto fade: applies fade in/out effects to video audio
   * - Smart track placement: prefers video tracks and handles collision detection
   *
   * @param element The video element to add
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @param options Video-specific options (volume, fit, loop, autoFade)
   * @returns AddVideoElementCommand instance if successful, null otherwise
   */
  commandAddVideoElement(
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
    options?: AddVideoElementOptions,
  ): AddVideoElementCommand | null {
    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddVideoElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
      options,
    );

    const success = this.executeCommand(command);
    return success ? command : null;
  }

  /**
   * Puts element into a stacked layout view
   */
  commandAddLayoutElements(
    elementId: string,
    layoutType: 'stacked-1-1' | 'stacked-1-3',
  ): [Element, Element] | null {
    const element = this.getElementById(elementId);
    if (!element) {
      return null;
    }

    const command = new AddLayoutElementsCommand(this, element, {
      layoutType,
    });

    const success = this.executeCommand(command);
    return success ? command.getResultElements() : null;
  }

  /**
   * Removes element from a stacked layout view
   */
  commandClearLayoutElements(
    elementId: string,
    fit: 'cover' | 'contain' | 'fill' = 'cover',
    videoSourceDimensionsPrev?: VideoDimensions,
    videoSourceDimensionsCurr?: VideoDimensions,
    naturalMediaDimensions: Record<string, NaturalDimensions> = {},
  ): boolean {
    const element = this.getElementById(elementId);
    if (!element) {
      return false;
    }

    const command = new ClearLayoutElementsCommand(this, element, {
      fit,
      videoSourceDimensionsPrev,
      videoSourceDimensionsCurr,
      naturalMediaDimensions,
    });
    const success = this.executeCommand(command);
    return success;
  }

  /**
   * Splits a stacked layout element at the specified time
   */
  commandSplitLayoutElements(
    elementId: string,
    splitTime: number,
  ): [SplitResultElements, SplitResultElements] | null {
    const element = this.getElementById(elementId);
    if (!element) {
      return null;
    }

    const command = new SplitLayoutElementsCommand(this, element, {
      splitTime,
    });
    const success = this.executeCommand(command);
    return success ? command.getResultElements() : null;
  }

  /**
   * Deletes a stacked layout element, along with its children and associated data
   */
  commandDeleteLayoutElements(elementId: string): boolean {
    const element = this.getElementById(elementId);
    if (!element) {
      return false;
    }

    const command = new DeleteLayoutElementsCommand(this, element);
    const success = this.executeCommand(command);
    return success;
  }

  /**
   * Trims a stacked layout element at the specified time
   */
  commandTrimLayoutElements(
    elementId: string,
    trimType: 'start' | 'end',
    trimTime: number,
  ): boolean {
    const element = this.getElementById(elementId);
    if (!element) {
      return false;
    }

    const command = new TrimLayoutElementsCommand(this, element, {
      trimType,
      trimTime,
    });
    const success = this.executeCommand(command);
    return success;
  }

  /**
   * Excises a stacked layout element at the specified time
   */
  commandExciseLayoutElements(
    elementId: string,
    mediaRemovalStart: number,
    mediaRemovalEnd: number,
  ): [ExciseResultElements, ExciseResultElements] | null {
    const element = this.getElementById(elementId);
    if (!element) {
      return null;
    }

    const command = new ExciseLayoutElementsCommand(this, element, {
      mediaRemovalStart,
      mediaRemovalEnd,
    });
    const success = this.executeCommand(command);
    return success ? command.getResultElements() : null;
  }

  /**
   * Merges two stacked layout elements
   */
  commandMergeLayoutElements(
    prev: Element,
    next: Element,
    track: Track,
  ): boolean {
    const command = new MergeLayoutElementsCommand(this, undefined, {
      prev,
      next,
      track,
    });
    const success = this.executeCommand(command);
    return success;
  }

  /**
   * Switches the stacked layout type
   */
  commandSwitchLayoutType(
    elementId: string,
    layoutType: 'stacked-1-1' | 'stacked-1-3',
    videoSourceDimensionsPrev?: VideoDimensions,
    videoSourceDimensionsCurr?: VideoDimensions,
    naturalMediaDimensions: Record<string, NaturalDimensions> = {},
  ): SwitchResultElements | null {
    const element = this.getElementById(elementId);
    if (!element) {
      return null;
    }

    const command = new SwitchLayoutTypeCommand(this, element, {
      layoutType,
      videoSourceDimensionsPrev,
      videoSourceDimensionsCurr,
      naturalMediaDimensions,
    });
    const success = this.executeCommand(command);
    return success ? command.getResultElements() : null;
  }

  /**
   * Gets a timeline element by ID
   */
  getElementById(elementId: string): Element | undefined {
    // find element by id or find nested element in composition
    for (const element of this.timeline.allElements) {
      if (element.id === elementId) {
        return element;
      }
      if (Element.isCompositionElement(element)) {
        const foundNestedElement = element.findElement(
          el => el.id === elementId,
        );
        if (foundNestedElement) {
          return foundNestedElement;
        }
      }
    }
  }

  /**
   * Gets a track by ID
   */
  getTrackById(trackId: string): Track | undefined {
    return this.timeline.tracks.find(track => track.id === trackId);
  }

  /**
   * Creates a renderer sync observer that automatically syncs changes to the renderer
   * This is an example of how to use the observer pattern for common operations
   */
  createRendererSyncObserver(): EditorObserver {
    return {
      onEditorChange: (editor, changeType) => {
        // We can filter which changes we want to react to
        // For renderer sync, we might want to respond to most changes
        if (changeType === EditorChangeType.TIMELINE_CHANGED) {
          VideoEditorLogger.logStateChange('sample_observer_sync', {
            changeType,
          });
        }
      },
    };
  }

  /**
   * Creates a transcript refresh observer that updates active transcript when relevant
   * Responds to changes in the video elements that affect which parts of the transcript are active
   */
  createTranscriptRefreshObserver(): EditorObserver {
    return {
      onEditorChange: (editor, changeType, payload) => {
        // TODO: Fine grain control over which changes trigger transcript refresh?
        if (changeType === EditorChangeType.PROJECT_LOADED) {
          // When a project is loaded, update the active transcript
          VideoEditorLogger.logTranscriptOperation('refresh_transcript', {
            trigger: 'project_load',
            payload,
          });
          this.updateActiveTranscript();
        } else if (changeType === EditorChangeType.TIMELINE_CHANGED) {
          // General timeline changes might affect active transcript
          VideoEditorLogger.logTranscriptOperation('refresh_transcript', {
            trigger: 'timeline_change',
            source: payload?.source,
            payload,
          });
          this.updateActiveTranscript();
        }
      },
    };
  }

  // Timeline Element Operations with Command Pattern

  /**
   * Moves an element to a new time position
   * Uses command pattern and delegates to TrackManager via MoveElementCommand
   * @param elementId The ID of the element to move
   * @param newStartTime The new start time for the element (in seconds)
   * @returns true if the operation was successful, false otherwise
   */
  commandShiftElement(elementId: string, newStartTime: number): boolean {
    // Resolve element and track if IDs were provided
    const element = this.getElementById(elementId);
    if (!element) return false;

    // Execute the move command with the resolved objects
    return this.executeCommand(
      new ShiftElementCommand(this, element, newStartTime),
    );
  }

  commandShiftMultipleElements(
    elementIds: string[],
    timeShift: number,
  ): boolean {
    return this.executeCommand(
      new ShiftMultipleElementsCommand(this, elementIds, timeShift),
    );
  }

  /**
   * Trims the start of an element (adjusts start time and duration)
   * Uses command pattern and delegates to TrackManager via TrimElementStartCommand
   * @param elementId The element or ID of the element to trim
   * @param newStartTime The new start time for the element (in seconds)
   * @returns true if the operation was successful, false otherwise
   */
  commandTrimElementStart(elementId: string, newStartTime: number): boolean {
    // Resolve element if an ID was provided
    const element = this.getElementById(elementId);

    if (!element) return false;

    return this.executeCommand(
      new TrimElementStartCommand(this, element, newStartTime),
    );
  }

  /**
   * Trims the end of an element (adjusts duration)
   * Uses command pattern and delegates to TrackManager via TrimElementEndCommand
   * @param elementId The element or ID of the element to trim
   * @param newEndTime The new end time for the element (in seconds)
   * @returns true if the operation was successful, false otherwise
   */
  commandTrimElementEnd(elementId: string, newEndTime: number): boolean {
    // Snap the time to frame if enabled
    const snappedTime = this.timeline.snapTimeToFrame(newEndTime);

    // Resolve element if an ID was provided
    const element = this.getElementById(elementId);

    if (!element) return false;

    return this.executeCommand(
      new TrimElementEndCommand(this, element, snappedTime),
    );
  }

  /**
   * Splits an element at the specified time
   * @param elementId The ID of the element to split
   * @param splitTime The time (in seconds) where the split should occur (playhead position)
   * @returns An array with the two resulting elements, or null if the operation failed
   */
  commandSplitElement(
    elementId: string,
    splitTime: number,
  ): [Element, Element] | null {
    const element = this.getElementById(elementId);
    if (!element) return null;

    // Validate that split time is within element bounds
    if (
      splitTime <= element.time ||
      splitTime >= element.time + element.durationSeconds
    ) {
      VideoEditorLogger.logTimelineOperation('split_element_error', {
        error: 'Split time must be within element bounds',
        element: element.id,
        elementTime: element.time,
        elementEnd: element.time + element.durationSeconds,
        requestedSplitTime: splitTime,
      });
      return null;
    }

    const command = new SplitElementCommand(this, element, splitTime);
    const success = this.executeCommand(command);

    return success ? command.getResultElements() : null;
  }

  /**
   * Shifts a track up in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  commandShiftTrackLayerUp(trackId: string): boolean {
    return this.executeCommand(new TrackLayerUpCommand(this, trackId));
  }

  /**
   * Shifts a track down in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  commandShiftTrackLayerDown(trackId: string): boolean {
    return this.executeCommand(new TrackLayerDownCommand(this, trackId));
  }

  /**
   * Sets the timeline duration
   * @param duration The new duration in seconds, or null to auto-calculate from elements
   * @returns true if successful, false otherwise
   */
  commandSetTimelineDuration(
    duration: number | null,
    shouldCreateUndoRedoEntry: boolean = true,
  ): boolean {
    return this.executeCommand(
      new SetTimelineDurationCommand(this, duration, shouldCreateUndoRedoEntry),
    );
  }

  /**
   * Crops the timeline to the inclusive range [keepStart, keepEnd]. Elements
   * entirely outside are removed; partially overlapping are trimmed and
   * shifted.
   * Wrapper around CropTimelineCommand.
   * @param keepStart start time in seconds (will be clamped to >=0)
   * @param keepEnd end time in seconds (if < keepStart it will be normalized)
   * @param trackIdsToSkip optional array of track IDs to skip cropping (e.g. tracks that were cropped already - i.e. original video track that was cropped by transcript remove command)
   * @returns true if successful, false otherwise
   */
  commandCropTimeline(
    keepStart: number,
    keepEnd: number,
    trackIdsToSkip: string[] = [],
  ): boolean {
    return this.executeCommand(
      new CropTimelineCommand(this, keepStart, keepEnd, trackIdsToSkip),
    );
  }

  /**
   * Triggers auto-calculation of timeline duration based on elements
   * This sets the duration to null, which causes Creatomate to calculate the duration
   * based on the furthest element end time
   * @returns true if successful, false otherwise
   */
  commandAutoCalculateTimelineDuration(): boolean {
    return this.executeCommand(new SetTimelineDurationCommand(this, null));
  }

  // TRANSCRIPT METHODS

  /**
   * Loads a transcript into the editor
   * @param transcriptElements Current project state transcript elements to load
   * @param originalTranscriptElements Original transcript elements to load
   */
  loadTranscript(
    transcriptElements: TranscriptElementLegacy[],
    originalTranscriptElements: TranscriptElementLegacy[],
    trackId?: string,
  ): boolean {
    // This is a special operation that replaces the entire transcript,
    // so we don't use a command for it
    this.legacyTranscript = new LegacyTranscript(
      originalTranscriptElements as TranscriptElement[],
    );
    this.originalTranscript = new Transcript(originalTranscriptElements);
    let videoTrack: Track | undefined;
    if (!trackId) {
      // If no track ID is provided, use the first video track
      videoTrack = this.timeline.getVideoTrack();
      if (!videoTrack) {
        console.warn('No video track available to attach transcript');
        return false;
      }
      trackId = videoTrack.id!;
    } else {
      videoTrack = this.getTrackById(trackId);
      if (!videoTrack || videoTrack.type !== 'video') {
        console.warn(
          `Track with ID ${trackId} is not a valid video track for transcript`,
        );
        return false;
      }
    }

    if (this.flags.enableTranscriptSegmentArchitecture) {
      const originalTokens = this.getOriginalTranscript()!;
      try {
        const transcriptTrack = TranscriptTrack.fromVideoTrackAndTranscript(
          videoTrack,
          transcriptElements,
          originalTokens,
        );

        const validationStartTime = performance.now();

        const validationResult = verifyTranscriptConsistencyQuick(
          transcriptElements,
          originalTokens,
          transcriptTrack,
        );

        const validationEndTime = performance.now();
        const validationDuration = validationEndTime - validationStartTime;
        console.info(
          `verifyTranscriptConsistency completed in ${validationDuration.toFixed(
            2,
          )}ms (status: ${validationResult.status})`,
        );
        if (this.flags.enableTranscriptMigrationValidation) {
          if (validationResult.status === 'error') {
            throw new TranscriptValidationError(
              'Transcript validation failed',
              validationResult.info,
            );
          }
        }

        // Replace the video track with the transcript track
        this.timeline.replaceTrack(trackId, transcriptTrack);
        console.debug('enableTranscriptSegmentArchitecture: transcript loaded');
      } catch (error) {
        if (error instanceof TranscriptValidationError) {
          throw error;
        }
        if (error instanceof Error) {
          console.warn(
            'Error creating transcript track, skipping load:',
            error.message,
          );
        } else {
          console.warn(
            'Unknown error creating transcript track, skipping load',
            error,
          );
        }
        return false;
      }
    }

    // Store the track ID for future reference
    this.setTranscriptTrack(trackId);

    // Notify observers about the transcript loading
    this.notifyObservers(EditorChangeType.TRANSCRIPT_LOADED, {
      transcript: this.flags.enableTranscriptSegmentArchitecture
        ? this.originalTranscript
        : this.legacyTranscript,
    });

    // Also notify about active transcript changing since it's derived from the original
    this.updateActiveTranscript();
    return true;
  }

  /**
   * Load karaoke data into the editor
   * Creates a KaraokeTrack with the specified track number (KARAOKE_TRACK_NUMBER)
   * The track will be automatically serialized when Timeline.toJSON() is called
   *
   * @param karaokeData - The karaoke data containing clips and configurations
   * @returns true if karaoke data was loaded successfully, false otherwise
   */
  loadKaraokeData(karaokeData: KaraokeData): boolean {
    try {
      VideoEditorLogger.logKaraoke('load_karaoke_data_begin', {
        configsCount: karaokeData.configs.size,
        defaultConfigId: karaokeData.defaultConfigId,
        clipsCount: karaokeData.clips.length,
      });

      const existingVideoTranscriptTrack = this.getVideoTranscriptTrack();
      if (!existingVideoTranscriptTrack) {
        VideoEditorLogger.logKaraoke('load_karaoke_data_error', {
          error: 'No video transcript track set',
        });
        console.error(
          'Cannot load karaoke data: No video transcript track set',
        );
        return false;
      }
      const activeTranscriptTokens = this.getActiveTranscriptNew();

      // Check if a karaoke track already exists
      const existingKaraokeTrack = this.timeline.getTrack(
        String(KARAOKE_TRACK_NUMBER),
      );

      if (existingKaraokeTrack) {
        VideoEditorLogger.logKaraoke('load_karaoke_data_replace_existing', {
          existingTrackId: existingKaraokeTrack.id,
        });
        // Remove existing karaoke track
        this.timeline.removeTrack(String(KARAOKE_TRACK_NUMBER));
        console.debug('Removed existing karaoke track');
      }

      // Create KaraokeTrack from karaoke data using the static factory method
      const karaokeTrack = KaraokeTrack.fromKaraokeDataAndTranscript(
        karaokeData,
        activeTranscriptTokens,
      );

      this.setKaraokeConfigs(karaokeData.configs);
      this.defaultKaraokeConfigId = karaokeData.defaultConfigId;
      // Add the karaoke track to the timeline
      this.timeline.addExistingTrack(karaokeTrack);

      VideoEditorLogger.logKaraoke('load_karaoke_data_success', {
        trackId: KARAOKE_TRACK_NUMBER,
        clipsCount: karaokeTrack.karaokeClips.length,
        configsCount: karaokeData.configs.size,
      });

      console.debug(
        `Karaoke track created with ID ${KARAOKE_TRACK_NUMBER} and ${karaokeTrack.karaokeClips.length} clips`,
      );

      // Notify observers about karaoke data loading
      this.notifyObservers(EditorChangeType.KARAOKE_LOADED, {
        karaokeData,
        trackId: String(KARAOKE_TRACK_NUMBER),
      });

      this.dirty = true;
      this.maybeSync();

      return true;
    } catch (error) {
      VideoEditorLogger.logKaraoke('load_karaoke_data_error', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      if (error instanceof Error) {
        console.error('Error loading karaoke data:', error.message);
      } else {
        console.error('Unknown error loading karaoke data', error);
      }
      return false;
    }
  }

  initializeDefaultKaraokeData(): boolean {
    VideoEditorLogger.logKaraoke('initialize_default_karaoke_data');
    const defaultKaraokeConfig = this.createKaraokeConfig(
      DEFAULT_EDITOR_KARAOKE_CONFIG,
    );
    this.defaultKaraokeConfigId = defaultKaraokeConfig.id;
    return true;
  }

  // /**
  //  * Removes a segment of transcript by adjusting video elements
  //  * @param wordIds Array of word IDs that represent the segment to remove
  //  * @returns true if successful, false otherwise
  //  */
  // commandRemoveTranscriptSegment(wordIds: string[]): boolean {
  //   const transcriptTrack = this.getTranscriptTrack();
  //   if (!transcriptTrack) {
  //     console.error(
  //       'Cannot remove transcript segment: No transcript track set',
  //     );
  //     return false;
  //   }

  //   return this.executeCommand(
  //     new TranscriptRemoveCommand(this, wordIds, transcriptTrack),
  //   );
  // }

  /**
   * Cut a segment of the transcript
   * @param segments Array of objects containing elementId and initialIndexes for segments to cut
   * @returns true if successful, false otherwise
   */
  commandCutTranscriptSegment(
    segments: CutTranscriptSegment[],
  ): RemovedSegment | null {
    // Store the cut indexes
    this.cutInitialIndexes = segments.flatMap(
      segment => segment.initialIndexes,
    );

    return this.commandRemoveTranscriptSegment(segments);
  }

  /**
   * Remove segments of transcript from one or more elements
   * @param segments Array of objects containing elementId and initialIndexes for segments to remove
   * @returns true if all segments were successfully removed, false otherwise
   * @deprecated
   */
  commandRemoveTranscriptSegment(
    segments: Array<{ elementId: string; initialIndexes: Array<number> }>,
  ): RemovedSegment | null {
    const commandsExecuted: TranscriptSimpleRemoveCommand[] = [];
    this.getActiveTranscriptLegacy(); // legacy transcript segments must be initialized by side effect
    this.executeInBatch(() => {
      let success = true;

      // Process each segment in the array
      for (const [idx, segment] of segments.entries()) {
        const { elementId, initialIndexes } = segment;

        if (!elementId || !initialIndexes || initialIndexes.length === 0) {
          VideoEditorLogger.logTranscriptOperation(
            'remove_transcript_segment_error',
            {
              error: 'invalid_segment',
              elementId,
              initialIndexes,
            },
          );
          success = false;
          continue;
        }

        // Find min and max indices for this segment
        const startInitialIndex = Math.min(...initialIndexes);
        const endInitialIndex = Math.max(...initialIndexes);

        // Create and execute the command for this segment
        const command = new TranscriptSimpleRemoveCommand(
          this,
          elementId,
          startInitialIndex,
          endInitialIndex,
          idx > 0, // do not trim extra before first segment
          idx < segments.length - 1, // do not trim extra after last segment
        );

        const result = this.executeCommand(command);
        commandsExecuted.push(command);
        // Track overall success - only successful if all segments are removed
        success = success && result;
      }

      return success;
    });
    const firstSegmentRemovedCommand = commandsExecuted.find(
      cmd => cmd.getTimelineSegmentRemoved()?.fromTs != null,
    );
    const lastSegmentRemovedCommand = [...commandsExecuted]
      .reverse()
      .find(cmd => cmd.getTimelineSegmentRemoved()?.toTs != null);
    const totalRemovedSegmentsDurationBeforeLastCommand =
      commandsExecuted.reduce(
        (total, cmd) =>
          cmd === lastSegmentRemovedCommand
            ? total
            : total +
              (cmd.getTimelineSegmentRemoved()?.toTs ?? 0) -
              (cmd.getTimelineSegmentRemoved()?.fromTs ?? 0),
        0,
      );
    return firstSegmentRemovedCommand && lastSegmentRemovedCommand
      ? {
          fromTs:
            firstSegmentRemovedCommand.getTimelineSegmentRemoved()!.fromTs,
          toTs:
            lastSegmentRemovedCommand.getTimelineSegmentRemoved()!.toTs +
            totalRemovedSegmentsDurationBeforeLastCommand,
        }
      : null;
  }

  commandRemoveTranscript(
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
    toClipboard: boolean = false,
  ): boolean {
    const commandsExecuted: (TranscriptRemoveCommand | RemoveElementCommand)[] =
      [];
    // TODO: validation of indexes (relative to segment, not absolute)
    if (!this.flags.enableTranscriptSegmentArchitecture) {
      VideoEditorLogger.logTranscriptOperation('segment_remove_error', {
        error: 'transcript_segment_architecture_disabled',
      });
      return false;
    }

    const success = this.executeInBatch(() => {
      let success = true;

      // Get transcript track to access segments in order
      const transcriptTrack = this.getTranscriptTrack();
      if (!transcriptTrack || !(transcriptTrack instanceof TranscriptTrack)) {
        VideoEditorLogger.logTranscriptOperation('segment_remove_error', {
          error: 'no_transcript_track',
        });
        return false;
      }

      // Get all transcript segment elements in timeline order
      const allSegmentElements = transcriptTrack.transcriptSegmentElements;

      // Find the segments to process (from fromSegmentId to toSegmentId)
      const fromSegmentIndex = allSegmentElements.findIndex(
        seg => seg.getClipTranscriptSegment().id === fromSegmentId,
      );
      const toSegmentIndex = allSegmentElements.findIndex(
        seg => seg.getClipTranscriptSegment().id === toSegmentId,
      );

      if (fromSegmentIndex === -1 || toSegmentIndex === -1) {
        VideoEditorLogger.logTranscriptOperation('segment_remove_error', {
          error: 'segment_not_found',
          fromSegmentId,
          toSegmentId,
        });
        return false;
      }

      // Ensure we process segments in the correct order
      const startIndex = Math.min(fromSegmentIndex, toSegmentIndex);
      const endIndex = Math.max(fromSegmentIndex, toSegmentIndex);
      const segmentsToProcess = allSegmentElements.slice(
        startIndex,
        endIndex + 1,
      );

      // Process each segment with appropriate trimHead/trimTail logic
      for (let i = 0; i < segmentsToProcess.length; i++) {
        const segmentElement = segmentsToProcess[i];

        // Skip segments that are already removed (RemovedTranscriptClip instances)
        if (!(segmentElement instanceof VideoTranscriptElement)) {
          continue;
        }

        // Get active elements to ensure this segment has content to remove
        const segmentWordsWithPunctuation = segmentElement
          .getClipTranscriptSegment()
          .getWordsWithPunctuation();
        if (segmentWordsWithPunctuation.length === 0) {
          // todo: delete element completely if it is in the middle and doesn't contain words

          const command = new RemoveElementCommand(
            this,
            (segmentElement as VideoTranscriptElement).id,
          );

          const result = this.executeCommand(command);
          commandsExecuted.push(command);
          success = success && result;
          continue;
        }

        // Determine trim parameters:
        // - trimHead: true for all segments except the first
        // - trimTail: true for all segments except the last
        const isFirstSegment = i === 0;
        const isLastSegment = i === segmentsToProcess.length - 1;
        const coversMediaIntro =
          isFirstSegment &&
          segmentElement.getMediaStartTime() === 0 &&
          segmentElement.getClipTranscriptSegment().firstWord &&
          segmentElement.getClipTranscriptSegment().firstWord!.relative_ts >
            this.config.maxMediaDurationToTrim;
        const coversMediaOutro =
          isLastSegment &&
          Math.abs(
            segmentElement.getMediaEndTime() - segmentElement.media_duration!,
          ) <
            1 / this.getFramerate() &&
          segmentElement.getClipTranscriptSegment().lastWord &&
          segmentElement.getClipTranscriptSegment().lastWord!.relative_end_ts <
            segmentElement.durationSeconds - this.config.maxMediaDurationToTrim;
        const trimHead = !coversMediaIntro;
        const trimTail = !coversMediaOutro;

        // Determine the range for this segment
        let segmentFromIndex: number;
        let segmentToIndex: number;
        const segmentId = segmentElement.getClipTranscriptSegment().id;

        if (segmentId === fromSegmentId) {
          // First segment: use provided fromIndex
          segmentFromIndex = fromIndex;
          segmentToIndex =
            segmentId === toSegmentId
              ? toIndex
              : segmentWordsWithPunctuation.length - 1;
        } else if (segmentId === toSegmentId) {
          // Last segment: use provided toIndex
          segmentFromIndex = 0;
          segmentToIndex = toIndex;
        } else {
          // Middle segment: remove all words
          segmentFromIndex = 0;
          segmentToIndex = segmentWordsWithPunctuation.length - 1;
        }

        // Create and execute the command for this segment
        const command = new TranscriptRemoveCommand(
          this,
          segmentId,
          segmentFromIndex,
          segmentToIndex,
          trimHead,
          trimTail,
        );

        const result = this.executeCommand(command);
        commandsExecuted.push(command);
        // Track overall success - only successful if all segments are processed
        success = success && result;
      }

      return success;
    });

    if (toClipboard && success) {
      this.cutPasteClipboardSegments = commandsExecuted
        .filter(
          (cmd): cmd is TranscriptRemoveCommand =>
            cmd instanceof TranscriptRemoveCommand,
        )
        .map(cmd => cmd.getOperationResult()?.removedSegment)
        .filter((seg): seg is ClipTranscriptSegment => seg != null);
    }

    return success;
  }

  /**
   * Paste segments from clipboard to a specific position in the transcript
   * @param clipSegmentId The ID of the segment to paste before/after
   * @param clipIndex The index within the segment (relative to wordsWithPunctuation)
   * @param after Whether to paste after (true) or before (false) the specified position
   * @returns true if paste was successful, false otherwise
   */
  commandPasteTranscript(
    clipSegmentId: string,
    clipIndex: number,
    after: boolean,
  ): boolean {
    if (!this.flags.enableTranscriptSegmentArchitecture) {
      VideoEditorLogger.logTranscriptOperation('segment_paste_error', {
        error: 'transcript_segment_architecture_disabled',
      });
      return false;
    }

    // Check if we have clipboard segments to paste
    const clipboardSegments = this.cutPasteClipboardSegments;
    if (!clipboardSegments || clipboardSegments.length === 0) {
      VideoEditorLogger.logTranscriptOperation('segment_paste_error', {
        error: 'empty_clipboard',
      });
      return false;
    }

    // Get transcript track to access segments
    const transcriptTrack = this.getTranscriptTrack();
    if (!transcriptTrack || !(transcriptTrack instanceof TranscriptTrack)) {
      VideoEditorLogger.logTranscriptOperation('segment_paste_error', {
        error: 'no_transcript_track',
      });
      return false;
    }

    // Create and execute the paste command
    const command = new TranscriptPasteCommand(
      this,
      clipboardSegments,
      clipSegmentId,
      clipIndex,
      after,
    );

    const success = this.executeCommand(command);

    if (success) {
      VideoEditorLogger.logTranscriptOperation('segment_paste_success', {
        clipSegmentId,
        clipIndex,
        after,
        segmentsCount: clipboardSegments.length,
      });
      this.cutPasteClipboardSegments = null; // Clear clipboard after pasting
    } else {
      VideoEditorLogger.logTranscriptOperation('segment_paste_error', {
        error: 'command_execution_failed',
        clipSegmentId,
        clipIndex,
        after,
      });
    }

    return success;
  }

  /**
   * Gets the current clipboard segments from cut operations
   * @returns Readonly array of clipboard segments, or empty array if none available
   */
  getClipboardSegments(): readonly ClipTranscriptSegment[] {
    return this.cutPasteClipboardSegments || [];
  }

  /**
   * Gets the currently cut transcript words' initial indexes
   * @returns Array of initial indexes that were cut, or empty array if none available
   */
  getCutTranscriptInitialIndexes(): number[] {
    return [...this.cutInitialIndexes];
  }

  // /**
  //  * Paste previously cut transcript words at a specific position
  //  * @param pasteAtWordId The word ID where to paste the cut segment
  //  * @returns true if successful, false otherwise
  //  */
  // commandPasteTranscriptWords(pasteAtWordId: string): boolean {
  //   if (this.cutTranscriptWordIds.length === 0) {
  //     console.warn('No cut words available to paste');
  //     return false;
  //   }

  //   const transcriptTrack = this.getTranscriptTrack();
  //   if (!transcriptTrack) {
  //     console.error('Cannot paste transcript words: No transcript track set');
  //     return false;
  //   }

  //   return this.executeCommand(
  //     new TranscriptInsertCommand(
  //       this,
  //       this.cutTranscriptWordIds,
  //       pasteAtWordId,
  //       transcriptTrack,
  //     ),
  //   );
  // }

  /**
   * Paste previously cut transcript words at a specific time position
   * @param time The time position to paste at
   * @param startInitialIndex The start initial index of the segment to paste
   * @param endInitialIndex The end initial index of the segment to paste
   * @returns true if successful, false otherwise
   * @deprecated
   */
  commandPasteTranscriptWords(
    pasteSegments: PasteTranscriptSegment[],
  ): boolean {
    if (this.cutInitialIndexes.length === 0) {
      VideoEditorLogger.logTranscriptOperation('paste_transcript_words_error', {
        error: 'no_cut_words',
      });
      return false;
    }

    this.getActiveTranscriptLegacy(); // legacy transcript segments must be initialized by side effect
    return this.executeInBatch(() => {
      let success = true;

      for (const segment of pasteSegments) {
        const { intoTime, startInitialIndex, endInitialIndex } = segment;
        if (startInitialIndex === undefined || endInitialIndex === undefined) {
          VideoEditorLogger.logTranscriptOperation(
            'paste_transcript_words_error',
            {
              error: 'invalid_segment',
              segment,
            },
          );
          success = false;
          continue;
        }

        // Create and execute the command for this segment
        const command = new TranscriptSimpleInsertCommand(
          this,
          intoTime,
          startInitialIndex,
          endInitialIndex,
        );
        const result = this.executeCommand(command);

        // Track overall success - only successful if all segments are inserted
        success = success && result;
      }

      return success;
    });
  }

  /**
   * Restore a previously removed transcript element, simply.
   */
  commandTranscriptSimpleRestore(
    prevElementId: string | null,
    nextElementId: string | null,
    startInitialIndex: number,
    endInitialIndex: number,
  ): ElementResizeResult | null {
    if (!this.legacyTranscript) {
      VideoEditorLogger.logTranscriptOperation('simple_restore_error', {
        error: 'no_transcript',
      });
      return null;
    }

    const transcriptSimpleRestoreCommand = new TranscriptSimpleRestoreCommand(
      this,
      prevElementId,
      nextElementId,
      startInitialIndex,
      endInitialIndex,
    );

    const success = this.executeCommand(transcriptSimpleRestoreCommand);
    return success
      ? transcriptSimpleRestoreCommand.getTimelineSegmentAdded()
      : null;
  }

  commandTranscriptRestore(
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
  ): boolean {
    if (!this.originalTranscript) {
      VideoEditorLogger.logTranscriptOperation('segment_restore_error', {
        error: 'no_transcript',
      });
      return false;
    }

    if (!this.flags.enableTranscriptSegmentArchitecture) {
      VideoEditorLogger.logTranscriptOperation('segment_restore_error', {
        error: 'transcript_segment_architecture_disabled',
      });
      return false;
    }

    // Handle multi-segment restoration
    if (fromSegmentId !== toSegmentId) {
      // Get all transcript track elements
      const transcriptTrack = this.getVideoTranscriptTrack();
      if (!transcriptTrack) {
        VideoEditorLogger.logTranscriptOperation('segment_restore_error', {
          error: 'no_transcript_track',
        });
        return false;
      }

      const allElements = transcriptTrack.transcriptSegmentElements;
      const fromElementIndex = allElements.findIndex(
        el => el.getClipTranscriptSegment().id === fromSegmentId,
      );
      const toElementIndex = allElements.findIndex(
        el => el.getClipTranscriptSegment().id === toSegmentId,
      );

      if (fromElementIndex === -1 || toElementIndex === -1) {
        VideoEditorLogger.logTranscriptOperation('segment_restore_error', {
          error: 'invalid_segment_ids',
          fromSegmentId,
          toSegmentId,
        });
        return false;
      }

      // Execute restore commands for each segment in the range
      return this.executeInBatch(() => {
        let success = true;

        for (let i = fromElementIndex; i <= toElementIndex; i++) {
          const element = allElements[i];
          const segmentId = element.getClipTranscriptSegment().id;
          const wordsWithPunctuation = element
            .getClipTranscriptSegment()
            .getWordsWithPunctuation();

          // Determine indices for this segment
          const startIdx = i === fromElementIndex ? fromIndex : 0;
          const endIdx =
            i === toElementIndex ? toIndex : wordsWithPunctuation.length - 1;

          const transcriptRestoreCommand = new TranscriptRestoreCommand(
            this,
            segmentId,
            startIdx,
            segmentId,
            endIdx,
          );

          const result = this.executeCommand(transcriptRestoreCommand);
          success = success && result;
        }

        return success;
      });
    }

    // Single segment restoration
    const transcriptRestoreCommand = new TranscriptRestoreCommand(
      this,
      fromSegmentId,
      fromIndex,
      toSegmentId,
      toIndex,
    );

    const success = this.executeCommand(transcriptRestoreCommand);
    return success;
  }

  commandChangeAspectRatio(
    prevVideoDimensions: VideoDimensions,
    newVideoDimensions: VideoDimensions,
    naturalMediaDimensions: Record<string, NaturalDimensions>,
    basicTextSetting: Record<string, string>,
  ): boolean {
    return this.executeCommand(
      new ChangeAspectRatioCommand(this, {
        prevVideoDimensions,
        newVideoDimensions,
        naturalMediaDimensions,
        basicTextSetting,
      }),
    );
  }

  /**
   * Gets the original, immutable transcript
   * @returns The original transcript elements, or undefined if no transcript is loaded
   */
  getOriginalTranscript(): ReadonlyArray<TranscriptElementLegacy> | undefined {
    if (!this.originalTranscript) return undefined;
    return this.originalTranscript.getOriginalTranscription();
  }

  /**
   * Gets the active transcript based on video elements from the transcript track
   * @returns The active transcript elements with modifications applied and timeline timestamps
   */
  getActiveTranscript(): TranscriptElementLegacy[] | undefined {
    if (this.flags.enableTranscriptSegmentArchitecture) {
      // Use the new architecture with TranscriptTrack
      return this.getActiveTranscriptNew();
    } else {
      return this.getActiveTranscriptLegacy();
    }
  }

  getActiveTranscriptLegacy(): TranscriptElement[] | undefined {
    if (!this.legacyTranscript) return undefined;

    // Get video elements from the designated transcript track if set, otherwise from all tracks
    let videoElements: VideoElement[] = [];

    const mainVideoTrack = this.timeline.getVideoTrack();
    if (mainVideoTrack) {
      videoElements = mainVideoTrack.elements.filter(
        (e): e is VideoElement => e.type === 'video',
      );
      console.log(
        `Using ${videoElements.length} video elements from transcript track ${this.transcriptTrackId}`,
      );
    }

    // Initialize each video element with the original transcription data
    // This ensures that transcript segments can be correctly derived
    // TODO: Find a better way to ensure transcrt track elements always ahve original transcription
    //        new TranscriptTrack object?
    const originalTranscription =
      this.legacyTranscript.getOriginalTranscription();
    videoElements.forEach(ve => {
      ve.setOriginalTranscription([...originalTranscription]);
    });

    // Get active transcript using the Transcript class's existing implementation
    const activeTranscript =
      this.legacyTranscript.getActiveTranscript(videoElements);
    return activeTranscript;
  }

  getActiveTranscriptNew() {
    let videoElements: readonly VideoTranscriptElement[] = [];

    const mainVideoTrack = this.timeline.getVideoTrack();
    if (mainVideoTrack && mainVideoTrack instanceof TranscriptTrack) {
      // only existing video elements
      videoElements = mainVideoTrack.elements;
      console.log(
        `Using ${videoElements.length} video elements from transcript track ${mainVideoTrack.id}`,
      );
    }

    const activeTranscript = videoElements.flatMap(ve =>
      ve.getActiveTranscriptElements(),
    );
    return activeTranscript;
  }

  /**
   * Gets the full transcript based on transcript segment elements from the transcript track
   * @returns The full transcript elements with modifications applied and timeline timestamps
   */
  getFullTranscript(): ActiveTranscriptTokenWithClipSegmentAttributes[] {
    if (!this.originalTranscript) return [];

    let transcriptSegments: readonly HasClipTranscriptSegment[] = [];

    const mainVideoTrack = this.timeline.getVideoTrack();
    if (mainVideoTrack && mainVideoTrack instanceof TranscriptTrack) {
      // all segments (video + removed clips)
      transcriptSegments = mainVideoTrack.transcriptSegmentElements;
      console.log(
        `Using ${transcriptSegments.length} TranscriptSegments elements from transcript track ${mainVideoTrack.id}`,
      );
    }

    // Get active transcript using the Transcript class's existing implementation
    const activeTranscript = transcriptSegments.flatMap(segment =>
      segment.getActiveTranscriptElements(),
    );

    if (this.isFlagOn('enableTranscriptSegmentArchitecture')) {
      return fillInWhitespacesInTranscriptElements(activeTranscript);
    }

    return activeTranscript;
  }

  /**
   * Get karaoke configurations as a Map
   * @returns Map of karaoke config ID to KaraokeConfig
   */
  getKaraokeConfigsMap(): Map<string, KaraokeConfig> {
    return this.karaokeConfigs;
  }

  /**
   * Set karaoke configurations
   * @param configs - Map of karaoke config ID to KaraokeConfig
   */
  setKaraokeConfigs(configs: Map<string, KaraokeConfig>): void {
    VideoEditorLogger.logKaraoke('set_karaoke_configs', {
      configsCount: configs.size,
      configIds: Array.from(configs.keys()),
    });
    this.karaokeConfigs = configs;
  }

  /**
   * Set the default karaoke config ID
   * @param configId - ID of the default karaoke config
   */
  setDefaultKaraokeConfigId(configId: string): void {
    VideoEditorLogger.logKaraoke('set_default_karaoke_config_id', {
      configId,
    });
    this.defaultKaraokeConfigId = configId;
  }

  /**
   * Get karaoke configurations (alias for getKaraokeConfigsMap)
   * @returns Map of karaoke config ID to KaraokeConfig
   */
  getKaraokeConfigs(): Map<string, KaraokeConfig> {
    return this.karaokeConfigs;
  }

  /**
   * Get karaoke lines from the karaoke track
   * @returns Array of karaoke lines, or empty array if no karaoke track exists
   */
  getKaraokeLines(): KaraokeLine[] {
    const karaokeTrack = this.getKaraokeTrack();
    if (!karaokeTrack) {
      return [];
    }
    const lines = karaokeTrack.karaokeClips.map(clip => clip.toKaraokeLine());
    return lines;
  }

  /**
   * Set default karaoke config by creating a new config or using existing one
   * @param config - Karaoke config without ID
   */
  setDefaultKaraokeConfig(config: Omit<KaraokeConfig, 'id'>): void {
    // Create a new config with the provided settings
    const newConfig = this.createKaraokeConfig(config);
    this.defaultKaraokeConfigId = newConfig.id;
    VideoEditorLogger.logKaraoke('set_default_karaoke_config', {
      configId: newConfig.id,
    });
  }

  /**
   * Get the default karaoke config
   * @returns The default KaraokeConfig or null if not set
   */
  getDefaultKaraokeConfig(): KaraokeConfig | null {
    if (!this.defaultKaraokeConfigId) {
      return null;
    }
    return this.karaokeConfigs.get(this.defaultKaraokeConfigId) || null;
  }

  /**
   * Create a new karaoke config
   * @param config - Karaoke config without ID
   * @returns The newly created KaraokeConfig with generated ID
   */
  createKaraokeConfig(config: Omit<KaraokeConfig, 'id'>): KaraokeConfig {
    // Get the KaraokeConfig ID generator (creates it if doesn't exist)
    const generator = IdGenerator.get(IdGeneratorEntityType.KaraokeConfig);
    const id = String(generator.getNextId());
    this.karaokeConfigs.set(id, { ...config, id });
    VideoEditorLogger.logKaraoke('create_karaoke_config', {
      configId: id,
    });
    return { ...config, id };
  }

  /**
   * Update an existing karaoke config
   * @param config - The karaoke config to update
   * @returns true if successful, false if config doesn't exist
   */
  updateKaraokeConfig(config: KaraokeConfig): boolean {
    if (!this.karaokeConfigs.has(config.id)) {
      VideoEditorLogger.logKaraoke('update_karaoke_config_error', {
        configId: config.id,
        error: 'Config does not exist',
      });
      console.warn(`Karaoke config with ID ${config.id} does not exist`);
      return false;
    }
    this.karaokeConfigs.set(config.id, config);
    VideoEditorLogger.logKaraoke('update_karaoke_config', {
      configId: config.id,
    });
    return true;
  }

  /**
   * Delete a karaoke config
   * @param configId - ID of the config to delete
   * @returns true if successful, false if config doesn't exist or is the default
   */
  deleteKaraokeConfig(configId: string): boolean {
    if (configId === this.defaultKaraokeConfigId) {
      VideoEditorLogger.logKaraoke('delete_karaoke_config_error', {
        configId,
        error: 'Cannot delete default config',
      });
      console.warn('Cannot delete the default karaoke config');
      return false;
    }
    if (!this.karaokeConfigs.has(configId)) {
      VideoEditorLogger.logKaraoke('delete_karaoke_config_error', {
        configId,
        error: 'Config does not exist',
      });
      console.warn(`Karaoke config with ID ${configId} does not exist`);
      return false;
    }
    const result = this.karaokeConfigs.delete(configId);
    VideoEditorLogger.logKaraoke('delete_karaoke_config', {
      configId,
      success: result,
    });
    return result;
  }

  /**
   * Get the karaoke track from the timeline
   * @returns The KaraokeTrack or undefined if not found
   */
  getKaraokeTrack(): KaraokeTrack | undefined {
    const track = this.timeline.getTrack(String(KARAOKE_TRACK_NUMBER));
    if (track instanceof KaraokeTrack) {
      return track;
    }
    return undefined;
  }

  commandReplaceTranscriptText(
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
    newTextValue: string,
  ): boolean {
    if (fromSegmentId === toSegmentId) {
      const command = new TranscriptTextReplaceCommand(
        this,
        fromSegmentId,
        fromIndex,
        toIndex,
        newTextValue,
      );
      return this.executeCommand(command);
    } else {
      const command = new TranscriptMultiSegmentTextReplaceCommand(
        this,
        fromSegmentId,
        fromIndex,
        toSegmentId,
        toIndex,
        newTextValue,
      );
      return this.executeCommand(command);
    }
  }

  commandEditTranscriptTime(
    transcriptTokenId: string,
    newMediaTime: { startTime: number; endTime: number },
  ): boolean {
    throw new Error('Method not implemented.');
  }

  commandAddTranscriptPunctuation(
    code: KnownPunctuation,
    segmentId: string,
    afterIndex: number,
  ): boolean {
    const command = new TranscriptAddPunctuationCommand(
      this,
      code,
      segmentId,
      afterIndex,
    );
    return this.executeCommand(command);
  }

  commandUpdateKaraokeConfig(config: KaraokeConfig): boolean {
    const command = new UpdateKaraokeConfigCommand(this, config);
    return this.executeCommand(command);
  }

  commandProduceKaraoke(options?: { replaceTrack: boolean }): boolean {
    // Check if KaraokeTrack already exists
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (existingKaraokeTrack && !options?.replaceTrack) {
      console.error('Cannot produce karaoke: KaraokeTrack already exists');
      return false;
    }

    if (existingKaraokeTrack) {
      // TODO: when only config should be changed, do not re-produce, just re-render
      // Remove existing karaoke track
      this.timeline.removeTrack(String(KARAOKE_TRACK_NUMBER));
      console.debug('Removed existing karaoke track');
    }

    const command = new ProduceKaraokeCommand(this, KARAOKE_TRACK_NUMBER);
    return this.executeCommand(command);
  }

  commandApplyKaraokeConfig(
    config: { id: string } | Omit<KaraokeConfig, 'id'>,
    karaokeClipIds?: string[],
  ): boolean {
    throw new Error('Method not implemented.');
  }

  commandAddBreak(transcriptTokenId: string): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error("Cannot add breaks: KaraokeTrack doesn't exists");
      return false;
    }
    const command = new AddKaraokeBreakCommand(this, transcriptTokenId);
    return this.executeCommand(command);
  }

  commandRemoveBreak(transcriptTokenId: string): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error("Cannot remove breaks: KaraokeTrack doesn't exists");
      return false;
    }
    const command = new RemoveKaraokeBreakCommand(this, transcriptTokenId);
    return this.executeCommand(command);
  }

  commandAdjustKaraokeClipTime(
    karaokeClipId: string,
    offset: { startSeconds?: number; endSeconds?: number },
  ): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error(
        "Cannot adjust karaoke clip time: KaraokeTrack doesn't exist",
      );
      return false;
    }
    const command = new AdjustKaraokeClipTimeCommand(
      this,
      karaokeClipId,
      offset,
      true,
    );
    return this.executeCommand(command);
  }

  commandHideInKaraoke(transcriptTokenIds: string[]): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error("Cannot hide in karaoke: KaraokeTrack doesn't exists");
      return false;
    }
    const command = new HideInKaraokeCommand(this, transcriptTokenIds);
    return this.executeCommand(command);
  }

  commandShowInKaraoke(transcriptTokenIds: string[]): boolean {
    const existingKaraokeTrack = this.getKaraokeTrack();
    if (!existingKaraokeTrack) {
      console.error("Cannot show in karaoke: KaraokeTrack doesn't exists");
      return false;
    }
    const command = new ShowInKaraokeCommand(this, transcriptTokenIds);
    return this.executeCommand(command);
  }

  /**
   * Updates the active transcript and notifies observers
   * This should be called whenever the timeline changes in a way that could affect
   * the active transcript (e.g., video elements added, modified, or removed)
   */
  public updateActiveTranscript(): void {
    if (!this.legacyTranscript) return;

    VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
      action: 'begin',
    });

    // Get and log video elements from the transcript track
    const mainVideoTrack = this.timeline.getVideoTrack();

    if (mainVideoTrack) {
      const videoElements = mainVideoTrack.elements
        .filter(e => e.type === 'video')
        .map(e => ({
          id: e.id,
          time: e.time,
          duration: e.duration,
          trim_start: (e as VideoElement).trim_start,
        }));
      VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
        action: 'track_video_elements',
        trackId: mainVideoTrack.id,
        videoElementCount: videoElements.length,
        videoElements,
      });
    } else {
      VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
        action: 'warning',
        message:
          'No transcript track set. Active transcript may be incomplete.',
      });

      // Log all video elements for debugging
      const allVideoElements = this.timeline.allElements
        .filter(e => e.type === 'video')
        .map(e => ({
          id: e.id,
          time: e.time,
          duration: e.duration,
          trim_start: (e as VideoElement).trim_start,
        }));
      VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
        action: 'all_video_elements',
        videoElementCount: allVideoElements.length,
        videoElements: allVideoElements,
      });
    }

    // Get active transcript based on current settings
    const activeTranscript = this.getActiveTranscript();
    VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
      action: 'result',
      activeTranscriptElementCount: activeTranscript?.length || 0,
    });

    // Also get the inferred transcript to see if it's working
    // const inferredTranscript = this.getInferredFullTranscript();
    // VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
    //   action: 'inferred_result',
    //   inferredTranscriptElementCount: inferredTranscript?.length || 0
    // });

    // Force a re-render by creating a new array instance even if the content is the same
    const forcedNewArray = activeTranscript ? [...activeTranscript] : [];

    // Notify observers about the active transcript changing
    this.notifyObservers(EditorChangeType.ACTIVE_TRANSCRIPT_CHANGED, {
      activeTranscript: forcedNewArray,
      // inferredTranscript: inferredTranscript ? [...inferredTranscript] : [],
    });
  }

  /**
   * Gets a transcript element by ID from the original transcript
   * @param id ID of the element to find (initial_index; word_index deprecated)
   * @returns The transcript element, or undefined if not found or no transcript is loaded
   */
  getTranscriptElementById(id: string): TranscriptElement | undefined {
    if (!this.legacyTranscript) return undefined;

    // First try to find by word_index (for transcript elements)
    const elementByWordIndex = this.legacyTranscript.getElementByWordIndex(id);
    if (elementByWordIndex) {
      return elementByWordIndex;
    }

    // If not found, fall back to initial_index lookup
    return this.legacyTranscript.getElementById(id);
  }

  /**
   * Begins a batch of operations, deferring renderer synchronization and observer notifications.
   * Maintains backward compatibility with the depth counter approach while using a simpler boolean flag.
   */
  beginBatch(): void {
    this.batchDepth++;
    this.inBatch = true;

    // Log batch operation
    VideoEditorLogger.logBatch('begin', this.batchDepth);
  }

  /**
   * Ends a batch of operations. Only syncs to renderer and processes notifications at the outermost batch level.
   * Maintains backward compatibility with the depth counter approach while using a simpler boolean flag.
   * @returns true if this was the outermost batch that completed, false for nested batches
   */
  endBatch(): boolean {
    if (this.batchDepth <= 0 || !this.inBatch) {
      throw new Error('No active batch to end');
    }

    // Decrement the batch depth counter
    const oldDepth = this.batchDepth;
    this.batchDepth--;

    // Log batch operation
    VideoEditorLogger.logBatch('end', this.batchDepth, {
      previousDepth: oldDepth,
    });

    // Only take action at the outermost level
    if (this.batchDepth === 0) {
      // Reset the inBatch flag at the outermost level
      this.inBatch = false;

      // Performance timing for syncing
      const syncStart = performance.now();
      let syncDuration = 0;

      // Only sync if changes were made during the batch
      if (this.dirty) {
        this.rendererSynchronizer.syncToRenderer();
        this.dirty = false;
        syncDuration = performance.now() - syncStart;

        // Log performance of sync operation
        VideoEditorLogger.logPerformance('renderer_sync', syncDuration);
      }

      // Add a batch completion notification to the queue
      this.observerManager.queueNotification(
        EditorChangeType.TIMELINE_CHANGED,
        {
          source: 'batch_complete',
          changes: 'multiple_operations',
        },
      );

      // Process all queued notifications after batch is complete and sync is done
      const notifStart = performance.now();
      this.observerManager.processQueuedNotifications();
      const notifDuration = performance.now() - notifStart;

      // Log performance of notification processing
      VideoEditorLogger.logPerformance('process_notifications', notifDuration);

      return true;
    }

    return false;
  }

  /**
   * Alias for endBatch to maintain compatibility
   */
  commitBatch(): boolean {
    return this.endBatch();
  }

  /**
   * Aborts the current batch operation, restoring the batch state without syncing
   */
  abortBatch(): void {
    if (this.batchDepth <= 0 || !this.inBatch) {
      throw new Error('No active batch to abort');
    }

    const oldDepth = this.batchDepth;

    // Log batch abort
    VideoEditorLogger.logBatch('abort', this.batchDepth, {
      reason: 'explicit_abort',
      previousDepth: oldDepth,
    });

    // Reset to no batching state
    this.batchDepth = 0;
    this.inBatch = false;
  }

  /**
   * Returns whether the editor is currently syncing to the renderer
   * Used by ObserverManager to determine if notifications should be queued
   */
  isSyncingToRenderer(): boolean {
    return this._isSyncingToRenderer;
  }

  /**
   * Sets a track as the designated transcript track
   * @param trackId The ID of the track to set as transcript track
   * @returns boolean indicating success
   */
  setTranscriptTrack(trackId: string): boolean {
    const track = this.getTrackById(trackId);
    if (!track) {
      VideoEditorLogger.logTranscriptOperation('set_transcript_track_error', {
        error: 'track_not_found',
        trackId,
      });
      return false;
    }

    if (this.flags.enableTranscriptSegmentArchitecture) {
      if (!(track instanceof TranscriptTrack)) {
        VideoEditorLogger.logTranscriptOperation('set_transcript_track_error', {
          error: 'not_transcript_track',
          trackId,
          expectedClass: 'TranscriptTrack',
          actualType: track.type,
          actualClass: track.constructor.name,
        });
        return false;
      }
    } else {
      if (track.type !== 'video') {
        VideoEditorLogger.logTranscriptOperation('set_transcript_track_error', {
          error: 'not_video_track',
          trackId,
          actualType: track.type,
        });
        return false;
      }
    }

    this.transcriptTrackId = trackId;
    VideoEditorLogger.logTranscriptOperation('set_transcript_track', {
      trackId,
    });
    return true;
  }

  /**
   * Gets the current transcript track
   * @returns The transcript track or undefined if not set
   */
  getTranscriptTrack(): Track | undefined {
    if (!this.transcriptTrackId) return undefined;
    return this.getTrackById(this.transcriptTrackId);
  }

  getVideoTranscriptTrack(): TranscriptTrack | undefined {
    // return first video track for now
    const mainVideoTrack = this.timeline.getVideoTrack();
    if (mainVideoTrack instanceof TranscriptTrack) {
      return mainVideoTrack;
    }
  }

  public getElementAtTime(
    trackId: string,
    time: number,
  ): Record<string, any> | null {
    // Find the element in the source that matches the given time
    const track = this.timeline.getTrack(trackId);
    if (!track) {
      VideoEditorLogger.logTimelineOperation('get_element_at_time_error', {
        error: 'track_not_found',
        trackId,
        time,
      });
      throw new Error(`Track with ID ${trackId} does not exist`);
    }

    const elements = track.elements;
    const element = elements.find(el => {
      return el.time <= time && time < el.end_time;
    });

    if (!element) {
      VideoEditorLogger.logTimelineOperation('get_element_at_time', {
        status: 'not_found',
        trackId,
        time,
        elementsInTrack: elements.length,
      });
      return null;
    }

    VideoEditorLogger.logTimelineOperation('get_element_at_time', {
      status: 'found',
      trackId,
      time,
      elementId: element.id,
      elementType: element.type,
    });
    return element || null;
  }

  public passExtraElementData(extraElementData: Video['extraElementData']) {
    this.trackManager.extraElementDataRaw = extraElementData;
  }

  public isOriginalVideoElement(elementId: string): boolean {
    const element = this.getElementById(elementId);
    if (!element || !Element.isVideoElement(element)) {
      return false;
    }
    return this.getTranscriptTrack()?.id === element.track?.toString();
  }

  public setFlag(flag: KnownFlags, value: boolean): void {
    console.log('VideoEditor: set flag', flag, value);
    this.flags[flag] = value;
  }

  public isFlagOn(flag: KnownFlags): boolean {
    return this.flags[flag] ?? false;
  }

  public getConfig(): VideoEditorConfig {
    return this.config;
  }

  public updateConfig(config: Partial<VideoEditorConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
